import { useEffect, useState } from 'react';
import { Alert, Modal, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InputField } from '@/components/ui/InputField';
import { Body, Caption, SubHeading } from '@/components/ui/Text';
import { useUserStore } from '@/state/userStore';
import { getUserTemplateRating, rateTemplate, type TemplateRating } from '@/services/templateRatings';
import { useToast } from '@/components/ui/ToastProvider';

type RateTemplateModalProps = {
  visible: boolean;
  onClose: () => void;
  template: {
    id: string;
    title: string;
  };
  tripId: string;
  source?: 'trip_card' | 'template_detail' | 'post_completion_prompt' | 'unknown';
  onRated?: (aggregate: { rating: number; reviewCount: number; submittedRating: number; hasReview: boolean }) => void;
  onDismiss?: () => void;
};

export function RateTemplateModal({
  visible,
  onClose,
  template,
  tripId,
  source = 'unknown',
  onRated,
  onDismiss,
}: RateTemplateModalProps) {
  const theme = useTheme();
  const { showSuccess } = useToast();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [existingRating, setExistingRating] = useState<TemplateRating | null>(null);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible || !firebaseUser?.uid || !template.id) return;
    let isMounted = true;
    setLoading(true);
    getUserTemplateRating(template.id, firebaseUser.uid)
      .then((existing) => {
        if (!isMounted) return;
        setExistingRating(existing);
        setRating(existing?.rating ?? 0);
        setReview(existing?.review ?? '');
      })
      .catch((error) => {
        console.warn('[RateTemplateModal] Failed to load existing rating', error);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [visible, firebaseUser?.uid, template.id]);

  const handleClose = () => {
    if (submitting) return;
    setHoveredRating(0);
    onDismiss?.();
    onClose();
  };

  const handleSubmit = async () => {
    if (!firebaseUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in to rate templates.');
      return;
    }
    if (rating < 1) {
      Alert.alert('Rating required', 'Please choose a rating from 1 to 5 stars.');
      return;
    }

    setSubmitting(true);
    try {
      const aggregate = await rateTemplate({
        templateId: template.id,
        tripId,
        rating,
        review,
        source,
      });
      onRated?.({
        rating: aggregate.rating,
        reviewCount: aggregate.reviewCount,
        submittedRating: rating,
        hasReview: Boolean(review.trim()),
      });
      setExistingRating({
        id: `${template.id}_${firebaseUser.uid}`,
        templateId: template.id,
        tripId,
        userId: firebaseUser.uid,
        rating,
        review: review.trim() || undefined,
        updatedAt: new Date(),
      });
      showSuccess(existingRating ? 'Template rating updated' : 'Template rating saved');
      setHoveredRating(0);
      onClose();
    } catch (error) {
      console.error('[RateTemplateModal] Failed to rate template', error);
      Alert.alert(
        'Could not save rating',
        error instanceof Error ? error.message : 'Please try again in a moment.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const displayRating = hoveredRating || rating;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <Pressable
        onPress={handleClose}
        style={{
          flex: 1,
          justifyContent: 'center',
          padding: theme.spacing(2),
          backgroundColor: theme.colors.modalOverlay,
        }}
      >
        <Pressable onPress={(event) => event.stopPropagation()}>
          <Card variant="elevated" style={{ maxHeight: '86%' }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: theme.spacing(1) }}>
              <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
                <SubHeading>{existingRating ? 'Update template rating' : 'Rate this template'}</SubHeading>
                <Body color={theme.colors.textSecondary}>{template.title}</Body>
              </View>
              <Pressable onPress={handleClose} hitSlop={8}>
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </Pressable>
            </View>

            {loading ? (
              <View style={{ paddingVertical: theme.spacing(3) }}>
                <Body color={theme.colors.textSecondary}>Loading rating...</Body>
              </View>
            ) : (
              <>
              <ScrollView
                style={{ marginTop: theme.spacing(2), maxHeight: 360 }}
                contentContainerStyle={{ gap: theme.spacing(2), paddingBottom: theme.spacing(1) }}
                keyboardShouldPersistTaps="handled"
              >
                <View style={{ gap: theme.spacing(1) }}>
                  <Body style={{ fontWeight: '600' }}>How useful was this template?</Body>
                  <View style={{ flexDirection: 'row', justifyContent: 'center', gap: theme.spacing(0.75) }}>
                    {[1, 2, 3, 4, 5].map((value) => (
                      <Pressable
                        key={value}
                        onPress={() => setRating(value)}
                        onHoverIn={() => setHoveredRating(value)}
                        onHoverOut={() => setHoveredRating(0)}
                        hitSlop={8}
                      >
                        <Ionicons
                          name={value <= displayRating ? 'star' : 'star-outline'}
                          size={34}
                          color={value <= displayRating ? theme.colors.warning : theme.colors.textSecondary}
                        />
                      </Pressable>
                    ))}
                  </View>
                  <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
                    {displayRating > 0 ? `${displayRating} out of 5` : 'Choose a rating'}
                  </Caption>
                  <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
                    Tap Save rating when you&apos;re done.
                  </Caption>
                </View>

                <InputField
                  label="Review (optional)"
                  placeholder="What made this template useful?"
                  value={review}
                  onChangeText={setReview}
                  multiline
                  numberOfLines={4}
                />

              </ScrollView>
              <View
                style={{
                  marginTop: theme.spacing(1.5),
                  paddingTop: theme.spacing(1.5),
                  borderTopWidth: 1,
                  borderTopColor: theme.colors.borderMuted,
                }}
              >
                <Button
                  label={submitting ? 'Saving rating...' : existingRating ? 'Update rating' : 'Save rating'}
                  onPress={handleSubmit}
                  disabled={submitting || rating < 1}
                  icon={<Ionicons name="checkmark-circle-outline" size={18} color={theme.colors.onPrimary} />}
                />
              </View>
              </>
            )}
          </Card>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
